import { useState, useEffect } from 'react';
import { AppConstants } from '@/app.constants';
import { idbStorage } from '@/store/idbStorage/idbStorage';
import { formatBytes, computeStringBytes } from '@/utils/bytes.util';

interface DBInfo {
  origin: string;
  bucketName: string;
  isPersistent: boolean;
  durability: string;
  quota: string;
  expiration: string;
  version: number;
  objectStores: number;
}

export enum IndexedDBDataType {
  Image,
  Inspection
}
interface TableData {
  key: string;
  value: string;
  type?: IndexedDBDataType
}

interface IndexedDBData {
  dbInfo: DBInfo;
  imagesData: TableData[];
  inspectionsData: TableData[];
  metaData: TableData[];
  savedInspectionsData: TableData[];
  imagesSize: string;
  inspectionsSize: string;
  metaDataSize: string;
  savedInspectionsSize: string;
  idbQuota: string;
  idbUsage: string;
  loading: boolean;
  error: string | null;
}

interface IndexedDBInfo extends IndexedDBData {
  refresh: () => Promise<void>;
}

export const useIndexedDBInfo = (): IndexedDBInfo => {
  const [data, setData] = useState<IndexedDBData>({
    dbInfo: {
      origin: '',
      bucketName: 'default',
      isPersistent: false,
      durability: 'relaxed',
      quota: '0 B',
      expiration: 'None',
      version: AppConstants.INDEX_DB.version,
      objectStores: Object.keys(AppConstants.INDEX_DB.store).length,
    },
    imagesData: [],
    inspectionsData: [],
    metaData: [],
    savedInspectionsData: [],
    imagesSize: '0 B',
    inspectionsSize: '0 B',
    metaDataSize: '0 B',
    savedInspectionsSize: '0 B',
    idbQuota: '0 B',
    idbUsage: '0 B',
    loading: true,
    error: null,
  });

  // formatBytes and computeStringBytes are imported from utils

  const computeValueBytes = (value: unknown): number => {
    try {
      const str = typeof value === 'string' ? value : JSON.stringify(value);
      return computeStringBytes(str);
    } catch {
      return 0;
    }
  };

  const getPersistentStatus = async (): Promise<boolean> => {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      try {
        return await navigator.storage.persisted();
      } catch (error) {
        console.warn('Could not get persistent status:', error);
        return false;
      }
    }
    return false;
  };

  const fetchDBInfo = async (): Promise<void> => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      // Get database info
      const isPersistent = await getPersistentStatus();
      const origin = window.location.origin;

      // Get all data for each store (for table rendering)
      const [imagesData, inspectionsData, masterData, savedInspectionsData] = await Promise.all([
        idbStorage.getAll(AppConstants.INDEX_DB.store.images),
        idbStorage.getAllKeys(AppConstants.INDEX_DB.store.inspections),
        idbStorage.getAll(AppConstants.INDEX_DB.store.masterData),
        idbStorage.getAll(AppConstants.INDEX_DB.store.savedInspections),
      ]);

      // Get all keys for each store
      const [imagesKeys, inspectionsKeys, masterDataKeys, savedInspectionsKeys] = await Promise.all([
        idbStorage.getAllKeys(AppConstants.INDEX_DB.store.images),
        idbStorage.getAllKeys(AppConstants.INDEX_DB.store.inspections),
        idbStorage.getAllKeys(AppConstants.INDEX_DB.store.masterData),
        idbStorage.getAllKeys(AppConstants.INDEX_DB.store.savedInspections),
      ]);

      // Fetch values again for accurate usage calculation (do not affect table rendering)
      const [imagesValues, inspectionsValues, masterValues, savedInspectionsValues] = await Promise.all([
        idbStorage.getAll(AppConstants.INDEX_DB.store.images),
        idbStorage.getAll(AppConstants.INDEX_DB.store.inspections),
        idbStorage.getAll(AppConstants.INDEX_DB.store.masterData),
        idbStorage.getAll(AppConstants.INDEX_DB.store.savedInspections),
      ]);

      const imagesUsedBytes = (imagesValues || []).reduce((sum: number, value: unknown) => sum + computeValueBytes(value), 0);
      const inspectionsUsedBytes = (inspectionsValues || []).reduce((sum: number, value: unknown) => sum + computeValueBytes(value), 0);
      const masterUsedBytes = (masterValues || []).reduce((sum: number, value: unknown) => sum + computeValueBytes(value), 0);
      const savedInspectionsUsedBytes = (savedInspectionsValues || []).reduce((sum: number, value: unknown) => sum + computeValueBytes(value), 0);

      const usedBytes = imagesUsedBytes + inspectionsUsedBytes + masterUsedBytes + savedInspectionsUsedBytes;
      const quota = formatBytes(usedBytes);

      // Browser storage estimate (overall, not just this DB)
      let estimatedQuota = 0;
      let estimatedUsage = 0;
      if (navigator?.storage?.estimate) {
        try {
          const estimate = await navigator.storage.estimate();
          estimatedQuota = typeof estimate.quota === 'number' ? estimate.quota : 0;
          estimatedUsage = typeof estimate.usage === 'number' ? estimate.usage : 0;
        } catch {}
      }

      // Helper function to convert store data to table format
      const convertStoreToTableData = (keys: any[], data: any[], dataType?: IndexedDBDataType): TableData[] => {
        const tableData: TableData[] = [];

        // Add first few actual keys from the store (limit to prevent UI overload)
        // const limitedKeys = keys.slice(0, 5); // Show first 5 keys
        keys.forEach((key, index) => {
          const keyStr = typeof key === 'string' ? key : key.toString();
          let valueStr = 'Data';

          // Try to get meaningful value preview
          if (data[index]) {
            try {
              const value = typeof data[index] === 'string' ? data[index] : JSON.stringify(data[index]);
              valueStr = value;
            } catch (e) {
              valueStr = 'Complex data';
            }
          }

          tableData.push({
            key: keyStr,
            value: valueStr,
            type: dataType
          });
        });

        // Add indicator if there are more keys
        // if (keys.length > 5) {
        //   tableData.push({
        //     key: '...',
        //     value: `${keys.length - 5} more items`
        //   });
        // }

        return tableData;
      };

      setData({
        dbInfo: {
          origin,
          bucketName: 'default',
          isPersistent,
          durability: 'relaxed',
          quota,
          expiration: 'None',
          version: AppConstants.INDEX_DB.version,
          objectStores: Object.keys(AppConstants.INDEX_DB.store).length,
        },
        imagesData: convertStoreToTableData(imagesKeys, imagesData, IndexedDBDataType.Image),
        inspectionsData: convertStoreToTableData(inspectionsKeys, inspectionsData),
        metaData: convertStoreToTableData(masterDataKeys, masterData),
        savedInspectionsData: convertStoreToTableData(savedInspectionsKeys, savedInspectionsData, IndexedDBDataType.Inspection),
        imagesSize: formatBytes(imagesUsedBytes),
        inspectionsSize: formatBytes(inspectionsUsedBytes),
        metaDataSize: formatBytes(masterUsedBytes),
        savedInspectionsSize: formatBytes(savedInspectionsUsedBytes),
        idbQuota: formatBytes(estimatedQuota),
        idbUsage: formatBytes(estimatedUsage),
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching IndexedDB info:', error);
      setData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch database info'
      }));
    }
  };

  useEffect(() => {
    fetchDBInfo();
  }, []);

  return {
    ...data,
    refresh: fetchDBInfo,
  };
}; 